import {
	Autocomplete,
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	TextField,
	Theme,
	Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useFormik } from 'formik'
import { useState } from 'react'
import * as yup from 'yup'
import {
	BaseEventListenerModel,
	EventHandler,
} from '../../../../api/clients/events/DTO'
import {
	FixedHeader,
	FixedHeaderWithHint,
} from '../../../../utils/HOC/FixedHeaders'

type CreateEventListenerDialogProps = {
	eventHandlers: EventHandler[]
	existingEventListenerNames: string[]
	events: string[]
	onConfirm: (v: BaseEventListenerModel) => void
	onCancel: () => void
}

export const CreateEventListenerDialog = (
	props: CreateEventListenerDialogProps,
) => {
	const styles = useStyles()

	const [selectedEventHandlerId, setSelectedEventHandlerId] = useState<string>(
		props.eventHandlers[0].eventHandlerId ?? undefined,
	)

	const [selectedEventId, setSelectedEventId] = useState<string | undefined>(
		undefined,
	)

	const validationSchema = yup.object({
		name: yup
			.string()
			.required('Name is a required field')
			.test(
				'is-unique',
				'An event listener with this name already exists in your organization',
				(value) => {
					return (
						value !== undefined &&
						!props.existingEventListenerNames
							.map((v) => v.toUpperCase().trim())
							.includes(value.toUpperCase().trim())
					)
				},
			),
		description: yup.string(),
	})

	const formik = useFormik({
		initialValues: {
			name: '',
			description: '',
		},
		validationSchema,
		onSubmit: (values) => {
			if (selectedEventHandlerId === undefined || selectedEventId === undefined)
				return

			props.onConfirm({
				displayName: values.name,
				description: values.description,
				eventHandlerId: selectedEventHandlerId,
				eventHandlerState: {},
				eventId: selectedEventId,
			})
		},
	})

	return (
		<>
			<DialogTitle>Create Event Listener</DialogTitle>
			<DialogContent>
				<FixedHeaderWithHint
					label="Name"
					hint={formik.touched.name && formik.errors.name}
					hintColor="error.main"
				>
					<TextField
						fullWidth
						id="name"
						name="name"
						value={formik.values.name}
						error={!!formik.errors.name && formik.touched.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						disabled={formik.isSubmitting}
						autoComplete="off"
					/>
				</FixedHeaderWithHint>
				<FixedHeaderWithHint
					label="Description"
					hint={formik.values.description && formik.touched.description}
					hintColor="error.main"
				>
					<TextField
						fullWidth
						id="description"
						name="description"
						value={formik.values.description}
						error={!!formik.errors.description && formik.touched.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						disabled={formik.isSubmitting}
						autoComplete="off"
					/>
				</FixedHeaderWithHint>
				<FixedHeader label="Event" className={styles.select}>
					<Autocomplete
						options={props.events}
						renderInput={(params) => <TextField {...params} />}
						onChange={(_, value) => {
							setSelectedEventId(value ?? undefined)
						}}
					/>
				</FixedHeader>
				<FixedHeader label="Event Handler" className={styles.select}>
					<Select
						fullWidth
						defaultValue={selectedEventHandlerId}
						value={selectedEventHandlerId}
						onChange={(evt) => setSelectedEventHandlerId(evt.target.value)}
					>
						{props.eventHandlers.map((eventHandler) => (
							<MenuItem
								key={eventHandler.eventHandlerId}
								value={eventHandler.eventHandlerId}
							>
								<Box display="flex" flexDirection="column">
									<Typography fontWeight="medium">
										{eventHandler.displayName}
									</Typography>
									<Typography fontStyle="italic" fontSize="small">
										{eventHandler.description}
									</Typography>
								</Box>
							</MenuItem>
						))}
					</Select>
				</FixedHeader>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={formik.submitForm}
					disabled={
						!formik.dirty ||
						!formik.isValid ||
						selectedEventHandlerId === undefined ||
						selectedEventId === undefined ||
						formik.isSubmitting
					}
				>
					Create
				</Button>
			</DialogActions>
		</>
	)
}

const useStyles = makeStyles((theme: Theme) => ({
	select: {
		padding: theme.spacing(2, 0),
	},
}))
